/**
 * The ONE place the app asks where the phone is.
 *
 * Two readers share it — Home's weather and the course pickers ("Near
 * you") — so the OS is asked once and one fix serves both.
 *
 * - In the phone apps it goes through @capacitor/geolocation. A WebView's own
 *   `navigator.geolocation` is refused there without a prompt unless the
 *   native shell is set up for it, which is why the weather chip used to be
 *   website-only.
 * - On the website it is `navigator.geolocation`, with the Permissions API
 *   telling us whether asking would prompt.
 *
 * Only a call with `prompt: true` may cause a permission prompt the person
 * has not already said yes to. Two places make one:
 * - HOME, for the weather, on every open (as it always has), unless the
 *   weather is already cached.
 * - The course picker's "Show courses near me" row, on a tap.
 * Once allowed, every reader gets a fix silently: the picker that follows
 * Home reuses the held fix, or reads a fresh one without asking.
 *
 * "Allowed" is ALSO remembered on the device (ALLOWED_KEY), because Safari —
 * the iPhone website and the home-screen app alike — does not persist a
 * website's location grant, so its Permissions API reads 'prompt' again to
 * someone who said yes on Home, and the picker fell back to its "Show courses
 * near me" row. A device that has given a fix before is read as allowed until
 * the OS says 'denied'; if Safari wants to confirm, the dialog it shows is one
 * the person already said yes to.
 *
 * The fix is held IN MEMORY for FRESH_MS and never written to storage. Low
 * accuracy throughout: a course is a kilometre wide.
 */
import { Capacitor } from '@capacitor/core';

const FRESH_MS = 10 * 60 * 1000;
const TIMEOUT_MS = 8000;
const OPTIONS = { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: FRESH_MS };

let held = null;      // { position, at }
let inflight = null;

const isNative = () =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Geolocation');

const plugin = async () => (await import('@capacitor/geolocation')).Geolocation;

const ALLOWED_KEY = 'location_allowed_v1';

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  clear: (k) => { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};

/** This device has handed over a fix before (see ALLOWED_KEY above). */
const wasAllowed = () => !!store.get(ALLOWED_KEY);

/**
 * May a reader take a fix without being the one that asks? Granted, or a
 * 'prompt' on a device that has said yes before.
 */
export const mayReadWithoutAsking = (state) =>
  state === 'granted' || (state === 'prompt' && wasAllowed());

/** The fix from the last FRESH_MS, synchronously — for a first paint. */
export function heldPosition() {
  return held && Date.now() - held.at < FRESH_MS ? held.position : null;
}

/**
 * 'granted' | 'prompt' | 'denied' | 'unavailable'. Never throws.
 * 'prompt' also covers "this browser can't say" — asking is then the only way
 * to find out, and only a tap does that.
 */
export async function permissionState() {
  try {
    if (isNative()) {
      const s = await (await plugin()).checkPermissions();
      if (s.location === 'granted' || s.coarseLocation === 'granted') return 'granted';
      if (s.location === 'denied' && s.coarseLocation !== 'prompt') {
        store.clear(ALLOWED_KEY);
        return 'denied';
      }
      return 'prompt';
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unavailable';
    if (!navigator.permissions?.query) return 'prompt';
    const s = await navigator.permissions.query({ name: 'geolocation' });
    if (s.state === 'denied') store.clear(ALLOWED_KEY);
    return s.state === 'granted' || s.state === 'denied' ? s.state : 'prompt';
  } catch {
    // iOS rejects checkPermissions outright when Location Services is off.
    return 'unavailable';
  }
}

const toPosition = ({ coords }) => ({
  lat: coords.latitude,
  lng: coords.longitude,
  accuracy: coords.accuracy ?? null,
});

async function locate(prompt) {
  if (isNative()) {
    const geo = await plugin();
    if (prompt) {
      const s = await geo.checkPermissions();
      if (s.location !== 'granted' && s.coarseLocation !== 'granted') {
        const r = await geo.requestPermissions({ permissions: ['location', 'coarseLocation'] });
        if (r.location !== 'granted' && r.coarseLocation !== 'granted') return null;
      }
    }
    return toPosition(await geo.getCurrentPosition(OPTIONS));
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve(toPosition(p)), reject, OPTIONS);
  });
}

/**
 * The phone's position as {lat, lng, accuracy}, or null. Never throws.
 *
 * Without `prompt`, returns null unless permission is ALREADY granted (or was
 * on this device — see ALLOWED_KEY), so a caller can never be the one that
 * pops a dialog the person has not already answered yes to.
 */
export async function getPosition({ prompt = false } = {}) {
  const fresh = heldPosition();
  if (fresh) return fresh;
  if (!prompt) {
    const state = await permissionState();
    if (!mayReadWithoutAsking(state)) return null;
    // A remembered yes the OS no longer reports: go through the ask, which the
    // apps need to re-request and Safari answers from its own memory if it can.
    if (state !== 'granted') prompt = true;
  }
  if (!inflight) {
    inflight = locate(prompt)
      .then((position) => {
        if (position) {
          held = { position, at: Date.now() };
          store.set(ALLOWED_KEY, String(Date.now()));
        }
        return position;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function resetDevicePositionForTests() {
  held = null;
  inflight = null;
}
